<template>
  <v-container fluid>
    <v-card class="elevation-1 mb-6">
      <v-card-title class="pa-7">
        Přehled zákazníků
        <v-spacer></v-spacer>

        <v-btn
          color="primary"
          height="40px"
          min-width="130px"
          :to="{ name: 'customers.add' }"
          depressed
        >
          Nový zákazník
        </v-btn>
      </v-card-title>

      <v-card-text>
        <CustomersFilters @filter="setFiltersAction" />

        <v-data-table
          v-model="customer.selected"
          :headers="userHeadersByRole"
          :items="customer.items"
          :options.sync="customer.options"
          :server-items-length="customer.serverItemsLength"
          :loading="customer.loading"
          item-key="id"
          class="elevation-0"
          show-select
        >
          <template v-slot:item.id="{ item }">
            <router-link
              :to="{
                name: 'customers.detail',
                params: {
                  customerId: item.id,
                },
              }"
            >
              {{ item.id }}
            </router-link>
          </template>
          <template v-slot:item.createdAt="{ item }">
            <DateTime :single-line="false" :datetime="item.createdAt" />
          </template>
          <template v-slot:item.phone="{ item }">
            {{ formatPhoneNumber(item.phone) }}
          </template>
          <template v-slot:item.revision.modifiedAt="{ item }">
            <DateTime :datetime="item.revision.modifiedAt" />
          </template>
          <template v-slot:item.user.role="{ item }">
            <UserRole v-if="item.user" :role="item.user.role" />
          </template>
          <template v-slot:item.user.status="{ item }">
            <UserStatus v-if="item.user" :status="item.user.status" />
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn-toggle color="primary" dense group>
              <v-btn
                :to="{ name: 'customers.edit', params: { customerId: item.id } }"
                color="blue-grey"
                text
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn color="blue-grey" text @click="removeCustomer(item.id)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-btn-toggle>
          </template>
          <template v-slot:footer>
            <CustomersExportFooter v-model="customerFooter" />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { setFilters } from '@/components/mixins/setFilters'
import CustomersFilters from '@/components/filters/CustomersFilters'
import DateTime from '@/components/DateTime'
import { formatPhoneNumber } from '@/components/mixins/formatPhoneNumber'
import CustomersExportFooter from '@/components/customer/CustomersExportFooter'
import UserRole from '@/components/customer/UserRole'
import UserStatus from '@/components/customer/UserStatus'

export default {
  name: 'Customers',
  components: {
    UserStatus,
    UserRole,
    CustomersExportFooter,
    DateTime,
    CustomersFilters,
  },
  mixins: [setFilters, formatPhoneNumber],
  data() {
    return {
      customer: {
        selected: [],
        headers: [
          {
            text: 'Datum',
            value: 'createdAt',
            roles: ['ROLE_ADMIN'],
            width: '150px',
          },
          {
            text: 'Id',
            value: 'id',
            roles: ['ROLE_ADMIN'],
            width: '100px',
          },
          {
            text: 'Jméno',
            value: 'name',
            roles: ['ROLE_ADMIN'],
            width: '15%',
          },
          {
            text: 'Příjmení',
            value: 'surname',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Telefon',
            value: 'phone',
            width: '140px',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'E-mail',
            value: 'email',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Role',
            value: 'user.role',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Stav',
            value: 'user.status',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: '',
            value: 'actions',
            width: '1px',
            sortable: false,
            roles: ['ROLE_ADMIN'],
          },
        ],
        items: [],
        options: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['createdAt'],
          sortDesc: [true],
        },
        loading: false,
        serverItemsLength: 0,
      },
      customerFooter: {
        totalElements: 0,
        selectedData: {
          params: {},
          items: [],
        },
      },
      cancel: null,
    }
  },
  computed: {
    ...mapState({
      authorities: (state) => state.auth.user.authorities,
    }),
    userHeadersByRole() {
      return this.customer.headers.filter((e) => e.roles.some((e) => this.authorities.includes(e)))
    },
  },
  watch: {
    'customer.options': {
      handler() {
        this.loadCustomers()
      },
    },
    '$route.query': {
      handler() {
        this.loadCustomers()
      },
    },
    'customer.selected': {
      handler() {
        this.customerFooter.selectedData.items = this.customer.selected
      },
    },
  },
  methods: {
    ...mapActions({
      searchCustomers: 'customer/searchCustomers',
      deleteCustomer: 'customer/deleteCustomer',
    }),
    loadCustomers() {
      this.customer.loading = true
      const searchParams = {
        filterParams: this.$route.query,
        options: this.customer.options,
      }
      this.customerFooter.selectedData.params = searchParams
      this.searchCustomers(searchParams)
        .then((response) => {
          this.customerFooter.totalElements = response.data.totalElements
          this.customer.items = response.data.items
          this.customer.serverItemsLength = response.data.totalElements
          this.cancel = null
        })
        .finally(() => {
          this.customer.loading = false
        })
    },
    setFiltersAction(filters) {
      this.customer.options.page = 1
      this.setFilters(filters)
    },
    removeCustomer(id) {
      this.$confirm('', {
        title: 'Smazat zákazníka a jeho objednávky?',
        buttonTrueText: 'Ano',
        buttonFalseText: 'Ne',
        icon: '',
        color: 'black',
      }).then((result) => {
        if (result) {
          this.customer.loading = true
          this.deleteCustomer({ id: id, deleteOrders: true })
            .then(() => {
              this.loadCustomers()
            })
            .finally(() => {
              this.customer.loading = false
            })
        }
      })
    },
  },
}
</script>
